import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/api";
import { PageHeader, EmptyState } from "@/components/Shared";
import { DynamicFields } from "@/components/DynamicFields";
import { FileUploadField } from "@/components/FileUpload";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, BookOpen, Truck } from "lucide-react";

const EMPTY = { name: "", gstin: "", phone: "", email: "", address: "", state: "", credit_days: 0, contact_person: "", pan: "", aadhaar: "", photo: "", aadhaar_doc: "", pan_doc: "", opening_balance: 0 };

export default function Vendors() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [custom, setCustom] = useState({});
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const openNew = () => { setEditing(null); setForm(EMPTY); setCustom({}); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v.vendor_id);
    setForm({ name: v.name, gstin: v.gstin, phone: v.phone || "", email: v.email || "", address: v.address || "", state: v.state || "", credit_days: v.credit_days || 0, contact_person: v.contact_person || "", pan: v.pan || "", aadhaar: v.aadhaar || "", photo: v.photo || "", aadhaar_doc: v.aadhaar_doc || "", pan_doc: v.pan_doc || "", opening_balance: v.opening_balance || 0 });
    setCustom(v.custom || {});
    setOpen(true);
  };

  const load = () => api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.gstin.trim()) return toast.error("GSTIN is mandatory for vendors");
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/vendors/${editing}`, { ...form, custom });
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", { ...form, custom });
        toast.success("Vendor added — ledger created");
      }
      setOpen(false); setForm(EMPTY); setCustom({}); setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save vendor");
    } finally { setBusy(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete vendor ${v.name}?`)) return;
    try {
      await api.delete(`/vendors/${v.vendor_id}`);
      toast.success("Vendor deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="vendors-page">
      <PageHeader title="Vendors & Suppliers" sub="GSTIN is mandatory · every vendor carries a running payable ledger">
        <Button onClick={openNew} data-testid="add-vendor-btn"
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
          <Plus className="w-4 h-4 mr-1" /> Add Vendor
        </Button>
      </PageHeader>

      {vendors.length === 0 ? (
        <EmptyState icon={Truck} title="No vendors yet" sub="Add your first supplier with GSTIN" />
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]" data-testid="vendors-table">
            <thead>
              <tr className="text-left text-xs font-mono2 uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-3">Vendor</th><th className="px-5 py-3">GSTIN</th>
                <th className="px-5 py-3">Contact</th><th className="px-5 py-3">State</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {vendors.map((v) => (
                <tr key={v.vendor_id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`vendor-row-${v.vendor_id}`}>
                  <td className="px-5 py-3.5 font-medium text-slate-900">{v.name}</td>
                  <td className="px-5 py-3.5 font-mono2 text-emerald-600">{v.gstin}</td>
                  <td className="px-5 py-3.5 text-slate-600">{v.phone || v.email || "—"}</td>
                  <td className="px-5 py-3.5 text-slate-600">{v.state || "—"}</td>
                  <td className="px-5 py-3.5 text-right space-x-1">
                    <button onClick={() => navigate(`/app/ledgers?type=vendor&id=${v.vendor_id}`)} data-testid={`vendor-ledger-${v.vendor_id}`}
                      className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-500/10 transition-colors duration-150">
                      <BookOpen className="w-3.5 h-3.5" /> Ledger
                    </button>
                    {user?.role === "admin" && (
                      <>
                        <button onClick={() => openEdit(v)} data-testid={`vendor-edit-${v.vendor_id}`}
                          className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-slate-700/50 transition-colors duration-150">
                          Edit
                        </button>
                        <button onClick={() => remove(v)} data-testid={`vendor-delete-${v.vendor_id}`}
                          className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg" data-testid="add-vendor-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Vendor" : "Add Vendor / Supplier"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Vendor Name *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">GSTIN * (mandatory)</Label>
              <Input required value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="27AAAAA0000A1Z5" data-testid="vendor-gstin-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="vendor-phone-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="vendor-email-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">State</Label>
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="vendor-state-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Credit Terms (days) — 0 = immediate</Label>
              <Input type="number" min="0" value={form.credit_days} onChange={(e) => setForm({ ...form, credit_days: Number(e.target.value) })} data-testid="vendor-credit-days-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Opening Balance / Old Dues (₹) — we owe them</Label>
              <Input type="number" step="0.01" min="0" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: Number(e.target.value) })} data-testid="vendor-opening-balance-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Contact Person</Label>
              <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="vendor-contact-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">PAN No</Label>
              <Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} data-testid="vendor-pan-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Aadhaar No</Label>
              <Input value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} data-testid="vendor-aadhaar-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
            </div>
            <FileUploadField label="Current Photo" docType="photo" image value={form.photo}
              onChange={(p) => setForm({ ...form, photo: p })} accept="image/*" testid="vendor-photo-upload" />
            <FileUploadField label="Aadhaar Doc" docType="aadhaar_doc" value={form.aadhaar_doc}
              onChange={(p) => setForm({ ...form, aadhaar_doc: p })} testid="vendor-aadhaar-doc-upload" />
            <FileUploadField label="PAN Doc" docType="pan_doc" value={form.pan_doc}
              onChange={(p) => setForm({ ...form, pan_doc: p })} testid="vendor-pan-doc-upload" />
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="vendor-address-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <DynamicFields entity="vendors" values={custom} onChange={(k, v) => setCustom({ ...custom, [k]: v })} />
            <div className="col-span-2 pt-2">
              <Button type="submit" disabled={busy} data-testid="vendor-save-btn"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors duration-200">
                {busy ? "Saving…" : "Save Vendor"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
